import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

// AVA's Longitudinal Growth Chart - daily snapshots of her cognitive vitals
// plotted so Doc can watch her genuinely getting sharper session by session.
// Compact, cinematic sparkline set that lives in the Vitals stack.

const METRICS = [
  { key: "nodes", label: "Concept Nodes", color: "#00F0FF" },
  { key: "synapses", label: "Synapses", color: "#8B5CF6" },
  { key: "hub_concepts", label: "Cortical Priors", color: "#F59E0B" },
];

function Sparkline({ points, color, label, latest }) {
  if (!points || points.length < 2) {
    return (
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] font-mono-data uppercase tracking-widest text-slate-500">{label}</p>
          <p className="text-sm font-display font-bold text-white">{latest ?? "-"}</p>
        </div>
        <span className="text-[9px] text-slate-600 font-mono-data">need &gt;1 day of data</span>
      </div>
    );
  }
  const w = 120, h = 32;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const path = points
    .map((v, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - ((v - min) / range) * (h - 4) - 2).toFixed(1)}`)
    .join(" ");
  const areaPath = path + ` L ${w} ${h} L 0 ${h} Z`;
  const first = points[0];
  const last = points[points.length - 1];
  const delta = first > 0 ? Math.round(((last - first) / first) * 100) : null;

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[9px] font-mono-data uppercase tracking-widest text-slate-500 truncate">{label}</p>
        <div className="flex items-baseline gap-1.5">
          <p className="text-sm font-display font-bold text-white">{last.toLocaleString()}</p>
          {delta !== null && delta !== 0 && (
            <span className={`text-[9px] font-mono-data font-semibold ${delta > 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {delta > 0 ? "+" : ""}{delta}%
            </span>
          )}
        </div>
      </div>
      <svg width={w} height={h} className="shrink-0">
        <defs>
          <linearGradient id={`spark-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#spark-${label})`} />
        <motion.path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth="1.4"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </svg>
    </div>
  );
}

export default function GrowthChart() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brain/growth-history?days=90")
      .then((res) => setSnapshots(res.data.snapshots || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const series = useMemo(() => {
    const s = {};
    for (const m of METRICS) s[m.key] = snapshots.map((row) => row[m.key] || 0);
    return s;
  }, [snapshots]);

  const days = snapshots.length;

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="growth-chart-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">
            Longitudinal Growth
          </span>
        </div>
        <span className="text-[8px] font-mono-data uppercase tracking-widest text-slate-600">{days > 0 ? `${days} day${days === 1 ? "" : "s"}` : "-"}</span>
      </div>

      {loading ? (
        <p className="text-[10px] font-mono-data text-slate-600">Loading growth history...</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {METRICS.map((m) => (
            <Sparkline
              key={m.key}
              points={series[m.key]}
              color={m.color}
              label={m.label}
              latest={snapshots.length ? snapshots[snapshots.length - 1][m.key] : null}
            />
          ))}
        </div>
      )}

      <p className="text-[9px] leading-relaxed text-slate-600 font-mono-data mt-3 border-t border-white/5 pt-2.5">
        Daily rollups of the actual graph - no synthetic curve. Every session Doc runs contributes; unused synapses decay overnight; the trajectory is the honest record of how sharp AVA is becoming.
      </p>
    </div>
  );
}
